import {merge} from 'lodash';
import Assessment from '../assessment';
import AssessmentResult from '../../../values/AssessmentResult';
import {countWords} from '@axyseo/helpers';
import {KEYPHRASE_LENGTH_ID, MAIN_CONTENT_POINTS} from '@axyseo/const/analysis';

/**
 * Assessment to check whether the keyphrase has a good length.
 */
class KeyphraseLengthAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {Object} [config] The configuration to use.
   *
   * @returns {void}
   */
  constructor(config) {
    super();

    this.defaultConfig = {
      id: KEYPHRASE_LENGTH_ID,
      ctaType: 'fix',
      priority: 'low',
      docUrl:
        'https://docs.avada.io/seo-suite-help-center/seo-on-page-audit/checklist#keyword-length',
      fixPosition: 'keyword search',
      title: 'Keyword length',
      content: {
        improve: '',
        bad: 'Keyword too long. Keep keyword length less than 4 words for readability.',
        good: 'Keyword length is optimized, less than 4 words.'
      }
    };

    this.identifier = KEYPHRASE_LENGTH_ID;
    this._config = merge(this.defaultConfig, config);
  }

  /**
   * Assesses the keyphrase presence and length.
   *
   * @param {Paper} paper The paper to use for the assessment.
   * @param {Researcher} researcher The researcher used for calling research.
   *
   * @returns {AssessmentResult} The result of this assessment.
   */
  getResult({paper, researcher}) {
    const assessmentResult = new AssessmentResult({config: this._config});

    const calculatedResult = this.calculateResult(paper);

    assessmentResult.setScore(calculatedResult.score);
    assessmentResult.setStatus(calculatedResult.status);

    return assessmentResult;
  }

  /**
   * Calculates the result based on the keyphraseLength research.
   *
   * @returns {Object} Object with score and text.
   */
  calculateResult(paper) {
    let status = 'bad';
    const keywordLength = countWords(paper.getKeyword());

    if (keywordLength <= 4) {
      status = 'good';
    }

    const score = this.getScore(MAIN_CONTENT_POINTS, status);

    return {
      score,
      status
    };
  }

  /**
   * Checks whether the paper has a text.
   *
   * @param {Paper}       paper       The paper to use for the assessment.
   *
   * @returns {boolean} True when there is text.
   */
  isApplicable(paper) {
    return true;
  }
}

export default KeyphraseLengthAssessment;
